import { runInAction } from 'mobx'
import { v4 as uuidv4 } from 'uuid'
import rootStore from '../..' // Import the root store
import { Notification } from '../../../cls/modules/notification'
import {
	getNotification,
	setNotification
} from '../../../api/modules/api-notifications'
import { cleanObject, warnPt } from '../../../config'
import { actSendMail, responseHasError } from '../../users/actions'
import { wrapWithLoading } from '../../../config/general-actions'
import dayjs from 'dayjs'
import { actSaveEntityForDemo } from '../../monthly-reports/actions'

const notificationsStore = rootStore.notifications

/**
 * @async
 * @function newNotification
 * @description Creates a new notification item with the specified ID and marks it as new.
 * @param {string} newId - The ID of the new notification item.
 * @returns {Promise<void>}
 */
export const newNotification = async (newId) => {
	warnPt(newId)
	runInAction(() => {
		const _storeItm = requireNotificationById(newId)
		_storeItm.setNewFlag()
	})
}

/**
 * @function requireNotificationById
 * @description Retrieves a notification item by its ID from the store. If it doesn't exist, creates a new notification item.
 * @param {string} notificationId - The ID of the notification item to retrieve.
 * @returns {Notification} The retrieved or newly created notification item.
 */
export const requireNotificationById = (notificationId) => {
	warnPt(notificationId)
	let notification = notificationsStore.notifications[notificationId]
	if (notification) {
		return notification
	}
	runInAction(() => {
		notificationsStore.notifications[notificationId] = new Notification(
			notificationId
		)
	})
	return notificationsStore.notifications[notificationId]
}

/**
 * @async
 * @function actLoadNotification
 * @description Loads a notification item from the server by its ID and updates the store.
 * @param { function } navigate - The navigation function.
 * @param {string} notificationId - The ID of the notification item to load.
 * @returns {Promise<void>}
 */
export const actLoadNotification = wrapWithLoading(
	async (notificationId) => {
		const _response = await getNotification(notificationId)

		const hasError = responseHasError(_response)
		if (!hasError && _response.status === 'success') {
			const { data } = _response
			runInAction(() => {
				const _storeItm = requireNotificationById(notificationId)
				_storeItm.updateFromServer(data || {})
			})
		} else {
			throw _response
		}
	},
	{ loadingMessage: 'Loading Notification' }
)

/**
 * @async
 * @function actSaveNotification
 * @description Saves a notification item to the server.
 * @param {string} notificationId - The ID of the notification item to save.
 * @param {Object} full - The full data of the notification item to save.
 * @returns {Promise<boolean>} True if the save was successful, false otherwise.
 */
export const actSaveNotification = wrapWithLoading(
	async (notificationId, full, demo = false) => {
		if (demo) {
			actSaveEntityForDemo(
				notificationId,
				full,
				requireNotificationById,
				rootStore.uiLstNotifications,
				false
			)

			return true
		}

		full = cleanObject(full)
		const _response = await setNotification(notificationId, full)

		const hasError = responseHasError(_response)
		if (hasError) {
			throw _response
		}
		return true // No exceptions were thrown, so return true
	},
	{ loadingMessage: 'Saving Notification' }
)

/**
 * Sends a notification to the user, which may involve sending an email and/or
 * storing the notification in the system. The decision is based on the logged-in
 * user's notification configuration and the notification type.
 *
 * @param {Object} loggedInUser - The user who is triggering the notification.
 * This object should contain the user's allowed notification permissions.
 * @param {string} email_to - The recipient's email address where the notification should be sent.
 * @param {string} for_user_id - The ID of the user for whom the notification is intended (the target user).
 * @param {string} entity_table - The type of entity (e.g., 'sprints', 'tasks', etc.) related to the notification.
 * @param {string} entity_uid - The unique identifier (UID) for the specific entity related to the notification.
 * @param {string} kind - The type of notification (e.g., 'update', 'new', 'progress').
 */
export const sendNotification = (
	loggedInUser,
	email_to,
	for_user_id,
	entity_table,
	entity_uid,
	kind
) => {
	// Fetch the notification settings for the logged-in user
	const notificationConfig = loggedInUser?.allowed?.notifications

	// Retrieve notification settings for the specified entity type and kind.
	const { shouldMailNotification, shouldStoreNotification } =
		getNotificationSettings(notificationConfig, entity_table, kind)

	// Adjust the base route for the link if the entity is related to "sprints_team"
	let baseRoute = entity_table
	if (entity_table == 'sprints_team') baseRoute = 'sprints'

	// Hardcoded http protocol
	const link = `http://${window.location.hostname}/${baseRoute}/${entity_uid}/view`

	const textByKind = () => {
		switch (kind) {
			case 'new':
				return 'has created a new item,'
			case 'update':
				return 'has updated the item,'
			case 'progress':
				return 'has made progress on item,'
			default:
				return 'has made an action on item,'
		}
	}
	// If email notifications are enabled, send an email to the specified recipient
	if (shouldMailNotification) {
		actSendMail({
			email: 'notifications@webackit.com',
			subject: 'Notification',
			comment: `${
				loggedInUser.name !== ''
					? loggedInUser.name
					: loggedInUser.email
			}, ${textByKind()} ${link} at ${dayjs().format('YYYY-MM-DD')}`,
			email_to: email_to
		})
	}

	// If storing notifications is enabled, save the notification in the system
	if (shouldStoreNotification) {
		const notificationId = uuidv4()
		actSaveNotification(notificationId, {
			for_user_id: for_user_id,
			entity_table: entity_table,
			entity_uid: entity_uid,
			kind: kind,
			progress: 'not_read'
		})
	}
}

/**
 * Determines notification settings based on entity and kind configuration.
 *
 * Only store notifications by default and don't send mail if the configuration
 * for the current entity and kind is not explicitly set.
 *
 * @param {Object} notificationConfig - The configuration object for notifications.
 * @param {string} entity_table - The entity table name.
 * @param {string} kind - The kind of notification.
 * @returns {Object} An object containing the notification settings.
 */
function getNotificationSettings(notificationConfig, entity_table, kind) {
	const mailConfig = notificationConfig?.[entity_table]?.[kind]?.mail
	const showConfig = notificationConfig?.[entity_table]?.[kind]?.show

	return {
		shouldMailNotification: mailConfig,
		shouldStoreNotification:
			showConfig || (mailConfig === undefined && showConfig === undefined)
	}
}
